import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { formatPercent } from './radarUtils';

const ListBlock = ({ title, items, ordenacao }) => {
  const sorted = useMemo(() => {
    const arr = [...(items || [])];
    if (ordenacao === 'estoque_asc') arr.sort((a, b) => Number(a.estoque_atual || 0) - Number(b.estoque_atual || 0));
    if (ordenacao === 'estoque_desc') arr.sort((a, b) => Number(b.estoque_atual || 0) - Number(a.estoque_atual || 0));
    return arr;
  }, [items, ordenacao]);

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="card-body p-0">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>EAN</th>
                <th>Produto</th>
                <th>Secao</th>
                <th>Estoque</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((item) => (
                <tr key={`${title}-${item.id_produto}`}>
                  <td>{item.codigoean || '-'}</td>
                  <td>{item.descricao}</td>
                  <td>{item.secao || '-'}</td>
                  <td>{item.estoque_atual}</td>
                </tr>
              ))}
              {!sorted.length && (
                <tr>
                  <td colSpan={4} className="text-center text-gray-500 py-6">
                    Sem itens.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SaudeEstoquePage = () => {
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [secao, setSecao] = useState('todas');
  const [ordenacao, setOrdenacao] = useState('estoque_desc');
  const [data, setData] = useState({ resumo: {}, listas: {} });

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/radar-lucro/saude-estoque');
      setData(response.data || { resumo: {}, listas: {} });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar saude do estoque');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const allItems = useMemo(() => {
    const lists = data.listas || {};
    return [
      ...(lists.estoque_negativo || []),
      ...(lists.sem_preco || []),
      ...(lists.sem_custo || []),
      ...(lists.sem_venda_90d_com_estoque || []),
    ];
  }, [data.listas]);

  const secoes = useMemo(() => {
    const set = new Set(allItems.map((i) => i.secao).filter(Boolean));
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [allItems]);

  const filterItems = (items) => {
    const texto = busca.trim().toLowerCase();
    return (items || []).filter((item) => {
      const matchBusca =
        !texto ||
        String(item.descricao || '').toLowerCase().includes(texto) ||
        String(item.codigoean || '').toLowerCase().includes(texto);
      const matchSecao = secao === 'todas' || (item.secao || '') === secao;
      return matchBusca && matchSecao;
    });
  };

  return (
    <div className="animate-fade-in space-y-4">
      <div className="rounded-xl border border-primary-200 bg-primary-50 p-4">
        <p className="text-sm font-semibold text-primary-800">Confiabilidade operacional</p>
        <p className="text-2xl font-bold text-primary-900 mt-1">{formatPercent(data.resumo?.confiabilidade_pct)}</p>
        <p className="text-sm text-primary-800 mt-1">Dados inconsistentes podem distorcer decisoes de compra, preco e caixa.</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saude do Estoque</h1>
          <p className="text-gray-600">Qualidade dos dados que sustentam as decisoes</p>
        </div>
        <button className="btn-secondary" onClick={loadData} disabled={loading}>
          Atualizar
        </button>
      </div>

      <div className="card">
        <div className="card-body grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="form-input" placeholder="Buscar por descricao ou EAN" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <select className="form-select" value={secao} onChange={(e) => setSecao(e.target.value)}>
            <option value="todas">Todas as secoes</option>
            {secoes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select className="form-select" value={ordenacao} onChange={(e) => setOrdenacao(e.target.value)}>
            <option value="estoque_desc">Estoque desc</option>
            <option value="estoque_asc">Estoque asc</option>
          </select>
          <div className="text-sm text-gray-600 flex items-center">Itens mapeados: {allItems.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-body grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <div>
            <p className="text-gray-500">Confiabilidade</p>
            <p className="font-semibold text-gray-900">{formatPercent(data.resumo?.confiabilidade_pct)}</p>
          </div>
          <div>
            <p className="text-gray-500">Estoque negativo</p>
            <p className="font-semibold text-gray-900">{data.resumo?.itens_estoque_negativo || 0}</p>
          </div>
          <div>
            <p className="text-gray-500">Sem preco</p>
            <p className="font-semibold text-gray-900">{data.resumo?.itens_sem_preco || 0}</p>
          </div>
          <div>
            <p className="text-gray-500">Sem custo</p>
            <p className="font-semibold text-gray-900">{data.resumo?.itens_sem_custo || 0}</p>
          </div>
          <div>
            <p className="text-gray-500">Sem venda 90d</p>
            <p className="font-semibold text-gray-900">{data.resumo?.itens_sem_venda_90d_com_estoque || 0}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ListBlock title="Estoque negativo" items={filterItems(data.listas?.estoque_negativo || [])} ordenacao={ordenacao} />
        <ListBlock title="Sem preco" items={filterItems(data.listas?.sem_preco || [])} ordenacao={ordenacao} />
        <ListBlock title="Sem custo" items={filterItems(data.listas?.sem_custo || [])} ordenacao={ordenacao} />
        <ListBlock title="Sem venda 90d com estoque" items={filterItems(data.listas?.sem_venda_90d_com_estoque || [])} ordenacao={ordenacao} />
      </div>
    </div>
  );
};

export default SaudeEstoquePage;
